import React, { useCallback, useEffect, useState } from "react";
import { Clock, LockKeyhole, RefreshCw, ShieldCheck, WalletCards, Zap } from "lucide-react";
import { PageShell } from "@/pages/dashboard/RolePages";
import api, { formatApiError } from "@/lib/api";

const fmt = (value) => new Intl.NumberFormat("es-CO", { maximumFractionDigits: 2 }).format(Number(value || 0));

const SERVICE_LABELS = {
  diagnostic: "Diagnóstico KD9",
  quick_consultation: "Consulta Rápida",
  consultant_chat: "Hablar con un Consultor",
  human_accompaniment: "Acompañamiento humano",
  video_call: "Videollamada",
  oracle_interpretation: "Oráculo · Interpretación",
};

const earningLabel = (row) => {
  if (row.type === "transfer_to_withdrawable_balance") return "Transferencia a saldo retirable";
  if (row.source_label) return row.source_label;
  return SERVICE_LABELS[row.service_type || row.source_type] || "Ganancia por servicio";
};

export const ConsultantEarnings = ({ director = false }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [percent, setPercent] = useState(50);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [exchange, setExchange] = useState({ cop_per_energy: 100, version: 1 });

  const load = useCallback(async () => {
    setError("");
    try {
      if (director) {
        const { data: result } = await api.get("/api/director/consultant-earnings/settings");
        setData(result);
        setDays(Number(result.hold_days ?? 7));
        setPercent(Number(result.earning_percent ?? 50));
      } else {
        const [earnings, rate] = await Promise.all([
          api.get("/api/consultant/earnings/me"),
          api.get("/api/finance/exchange-rate"),
        ]);
        setData(earnings.data);
        setExchange(rate.data);
      }
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [director]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async (e) => {
    e.preventDefault(); setBusy(true); setError(""); setNotice("");
    try {
      const { data: result } = await api.put("/api/director/consultant-earnings/settings", { hold_days: Number(days), earning_percent: Number(percent) });
      setData(result); setNotice("Regla de ganancias actualizada.");
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const reconcile = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const { data: result } = await api.post("/api/director/consultant-earnings/reconcile");
      setNotice(`Reconciliación completada: ${result.credited} acreditada(s), ${result.failed} pendiente(s), ${result.scanned} revisada(s).`);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const transfer = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const key = globalThis.crypto?.randomUUID?.() || `earn-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const { data: result } = await api.post("/api/consultant/earnings/transfer", { idempotency_key: key });
      setNotice(`${fmt(result.amount)} ${result.unit} pasaron a tu saldo retirable. Equivalencia actual: 1 KD = ${new Intl.NumberFormat("es-CO").format(result.cop_per_energy)} COP; valor de referencia: ${new Intl.NumberFormat("es-CO").format(result.cop_equivalent)} COP.`);
      await load();
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (director) return <PageShell kicker="Director · Gobierno de Energía" title="CONFIGURAR MIS GANANCIAS"
    desc="Define qué porcentaje de la Energía KD9 pagada por servicios del Consultor corresponde a Mis Ganancias y cuántos días permanece bloqueado.">
    {error && <div className="kd9-card p-4 mb-4 text-red-300">{error}</div>}
    {notice && <div className="kd9-card p-4 mb-4 text-emerald-300">{notice}</div>}
    <form onSubmit={saveSettings} className="kd9-card p-6 max-w-xl">
      <div className="flex items-center gap-3 mb-5"><ShieldCheck/><div><div className="font-cinzel kd9-gold-text">REGLA DE GANANCIAS</div><p className="text-sm opacity-70">Cada acreditación guarda porcentaje y plazo como snapshot; los cambios futuros no alteran movimientos históricos.</p></div></div>
      <label className="block text-sm mb-2">Porcentaje para el Consultor</label>
      <input className="kd9-input w-full p-3 mb-4" type="number" min="1" max="100" value={percent} onChange={(e)=>setPercent(e.target.value)} required/>
      <label className="block text-sm mb-2">Días de bloqueo</label>
      <input className="kd9-input w-full p-3" type="number" min="0" max="3650" value={days} onChange={(e)=>setDays(e.target.value)} required/>
      <button disabled={busy} className="kd9-btn-primary w-full py-3 rounded-lg mt-5">{busy?"Guardando…":"Guardar configuración"}</button>
      <button type="button" disabled={busy} onClick={reconcile} className="kd9-btn-outline w-full py-3 rounded-lg mt-3">{busy?"Procesando…":"Reconciliar servicios completados sin ganancia"}</button>
    </form>
  </PageShell>;

  const wallet = data?.wallet || {};
  return <PageShell kicker="Consultor · Energía KD9" title="MIS GANANCIAS"
    desc="Ganancias por servicios KD9 completados. Nacen bloqueadas según la regla vigente, luego maduran y puedes pasarlas al saldo retirable; no se mezclan con Energía comprada o promocional.">
    {error && <div className="kd9-card p-4 mb-4 text-red-300">{error}</div>}
    {notice && <div className="kd9-card p-4 mb-4 text-emerald-300">{notice}</div>}
    <div className="grid sm:grid-cols-3 gap-4 mb-6">
      <div className="kd9-card p-5"><LockKeyhole className="mb-3"/><div className="text-xs uppercase opacity-60">Bloqueadas</div><strong className="text-2xl kd9-gold-text">{fmt(wallet.locked)} {wallet.unit}</strong></div>
      <div className="kd9-card p-5"><Zap className="mb-3"/><div className="text-xs uppercase opacity-60">Disponibles para pasar</div><strong className="text-2xl kd9-gold-text">{fmt(wallet.available)} {wallet.unit}</strong></div>
      <div className="kd9-card p-5"><WalletCards className="mb-3"/><div className="text-xs uppercase opacity-60">Transferidas a Wallet</div><strong className="text-2xl kd9-gold-text">{fmt(wallet.transferred)} {wallet.unit}</strong></div>
    </div>
    <div className="kd9-card p-4 mb-4 text-sm"><strong className="kd9-gold-text">Regla actual:</strong> {fmt(data?.settings?.earning_percent || 50)}% por servicio liquidable completado · bloqueo {data?.settings?.hold_days ?? 7} día(s).</div>\n    <div className="kd9-card p-4 mb-4 text-sm"><strong className="kd9-gold-text">Equivalencia de retiro:</strong> 1 KD = ${new Intl.NumberFormat("es-CO").format(exchange.cop_per_energy || 100)} COP · Solo aplica al saldo retirable y cada solicitud congela la tasa vigente.</div>\n    <button disabled={busy || Number(wallet.available||0)<=0} onClick={transfer} className="kd9-btn-primary px-5 py-3 rounded-lg mb-7">{busy?"Procesando…":"Pasar saldo maduro a saldo retirable"}</button>
    <div className="flex items-center justify-between mb-3"><h3 className="font-cinzel kd9-gold-text">MOVIMIENTOS</h3><button onClick={load} className="kd9-btn-outline p-2 rounded-lg"><RefreshCw size={15}/></button></div>
    <div className="space-y-3">{(data?.entries||[]).map((row)=><article key={row.id} className="kd9-card p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div>
        <div className="font-cinzel text-sm">{earningLabel(row)}</div>
        <div className="text-xs opacity-60">
          Referencia: {row.source_id || row.diagnostic_id || "—"} · Responsable: {row.responsible_id || "Sistema"}
        </div>
        {row.type !== "transfer_to_withdrawable_balance" && Number(row.gross_energy || 0) > 0 && row.earning_percent != null && (
          <div className="text-xs opacity-60 mt-1">
            Base: {fmt(row.gross_energy)} {row.unit} · Regla aplicada: {fmt(row.earning_percent)}% · bloqueo {row.hold_days ?? 0} día(s)
          </div>
        )}
        {row.type === "oracle_interpretation_earning" && (
          <div className="text-xs opacity-60 mt-1">
            Recompensa fija del Oráculo · bloqueo {row.hold_days ?? 0} día(s)
          </div>
        )}
      </div>
      <div className="sm:text-right"><strong className="kd9-gold-text">{fmt(row.amount)} {row.unit}</strong><div className="text-xs opacity-60 flex items-center gap-1 sm:justify-end"><Clock size={11}/>{row.status==="locked"&&row.unlock_at?`Disponible ${new Date(row.unlock_at).toLocaleString()}`:row.status}</div></div>
    </article>)}</div>
  </PageShell>;
};

export default ConsultantEarnings;
